import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import YearlyInvoiceTrendChart from "./YearlyInvoiceTrendChart";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const s = String(dateString);
    if (s.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = s.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

// Format currency: full number below 1M, compact (1M, 1.5B) only for 1M and above — exported for use in CustomerInvoices
export const formatCurrency = (value) => {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  if (Math.abs(num) >= 1_000_000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(num);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const getMetricsMonthLabel = (startDate) => {
  if (!startDate) return null;
  const parts = String(startDate).split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) return null;
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const MetricValue = ({ isLoading, children }) =>
  isLoading ? (
    <CircularProgress size={24} sx={{ color: "#3b82f6", mt: 0.5 }} />
  ) : (
    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
      {children}
    </Typography>
  );

const MetricCell = ({ label, sublabel, isLoading, value, valueNode }) => (
  <Box>
    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>
      {label}
      {sublabel ? (
        <Box component="span" sx={{ fontWeight: 500, opacity: 0.85, textTransform: "none", ml: 0.5 }}>
          {sublabel}
        </Box>
      ) : null}
    </Typography>
    <MetricValue isLoading={isLoading}>{valueNode ?? value}</MetricValue>
  </Box>
);

/**
 * Customer Invoices metrics — three panels on one row:
 * Box 1: Month/Year — invoices (YTD), latest invoice, net usage, fixed charges (last month)
 * Box 2: Year to date — total billed, net usage, fixed charges
 * Box 3: Yearly invoice trend chart
 */
const CustomerInvoicesMetrics = ({
  totalInvoices,
  totalBilled,
  latestInvoiceAmount,
  lastInvoicePeriodStart,
  customerMetrics,
  isFetchingCustomerMetrics,
  customerMetricsError,
}) => {
  const monthLabel = getMetricsMonthLabel(customerMetrics?.start_date) || "Current month";

  return (
    <Box sx={{ mb: 2 }}>
      {customerMetricsError && (
        <Box sx={{ p: 1.5, mb: 1.5, borderRadius: "6px", bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
          <Typography sx={{ fontSize: "0.8125rem", color: "#b91c1c" }}>
            {typeof customerMetricsError === "string"
              ? customerMetricsError
              : getApiErrorMessage(customerMetricsError) || "Failed to load metrics."}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {/* Box 1 — Month / Year */}
        <Box
          sx={{
            position: "relative",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: "#3b82f6" }} />
          <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 2,
              }}
            >
              {monthLabel}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 2 }}>
              <MetricCell
                label="Total Invoices Approved & Sent"
                sublabel="(YTD)"
                isLoading={isFetchingCustomerMetrics}
                value={totalInvoices != null ? totalInvoices : "—"}
              />
              <MetricCell
                label="Latest Invoice Amount"
                isLoading={isFetchingCustomerMetrics}
                valueNode={
                  <>
                    {formatCurrency(latestInvoiceAmount)}
                    {lastInvoicePeriodStart && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.25 }}>
                        <CalendarMonthIcon sx={{ fontSize: 12, color: "#64748b" }} />
                        <Typography sx={{ fontSize: "0.6875rem", color: "#64748b", fontWeight: 400 }}>
                          {formatDate(lastInvoicePeriodStart)}
                        </Typography>
                      </Box>
                    )}
                  </>
                }
              />
              <MetricCell
                label="Net Usage Charges"
                isLoading={isFetchingCustomerMetrics}
                value={formatCurrency(customerMetrics?.net_usage_last_month)}
              />
              <MetricCell
                label="Fixed Charges"
                isLoading={isFetchingCustomerMetrics}
                value={formatCurrency(customerMetrics?.fixed_charges_last_month)}
              />
            </Box>
          </Box>
        </Box>

        {/* Box 2 — Year to date */}
        <Box
          sx={{
            position: "relative",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: "#64748b" }} />
          <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 2,
              }}
            >
              Year to date
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
              <MetricCell
                label="Total Billed"
                isLoading={isFetchingCustomerMetrics}
                value={formatCurrency(totalBilled)}
              />
              <MetricCell
                label="Net Usage Charges"
                isLoading={isFetchingCustomerMetrics}
                value={formatCurrency(customerMetrics?.net_usage_ytd)}
              />
              <MetricCell
                label="Fixed Charges"
                isLoading={isFetchingCustomerMetrics}
                value={formatCurrency(customerMetrics?.fixed_charges_ytd)}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", width: "100%", minHeight: "100%" }}>
          <YearlyInvoiceTrendChart
            trendData={customerMetrics?.yearly_invoice_trend}
            isLoading={isFetchingCustomerMetrics}
            avgMonthlySpend={customerMetrics?.avg_monthly_spend}
            fillHeight
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerInvoicesMetrics;
